/**
 * Validation utility for course data
 */

export interface ValidationError {
  field: string;
  message: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
}

/**
 * Validates a course object
 * @param course The course object to validate
 * @param rowIndex The row index in the Excel file (for error messages)
 * @returns Validation result with any errors
 */
export const validateCourse = (course: any, rowIndex: number): ValidationResult => {
  const errors: ValidationError[] = [];
  const rowDisplay = rowIndex + 2; // +2 for header row and 0-indexing

  // Required fields
  if (!course.courseName) {
    errors.push({
      field: "courseName",
      message: `Row ${rowDisplay}: Course Name is required`,
    });
  } else if (typeof course.courseName !== "string") {
    errors.push({
      field: "courseName",
      message: `Row ${rowDisplay}: Course Name must be text`,
    });
  }

  // Course Type validation
  if (!course.courseType) {
    errors.push({
      field: "courseType",
      message: `Row ${rowDisplay}: Course Type is required`,
    });
  } else if (!["Core", "General", "Elective"].includes(course.courseType)) {
    errors.push({
      field: "courseType",
      message: `Row ${rowDisplay}: Course Type must be one of: Core, General, Elective`,
    });
  }

  // Cost validation (if provided)
  if (course.cost !== undefined && course.cost !== null) {
    const costValue = parseFloat(course.cost);
    if (isNaN(costValue)) {
      errors.push({
        field: "cost",
        message: `Row ${rowDisplay}: Cost must be a valid number`,
      });
    } else if (costValue < 0) {
      errors.push({
        field: "cost",
        message: `Row ${rowDisplay}: Cost cannot be negative`,
      });
    }
  }

  // Start Date validation (if provided)
  if (course.startDate) {
    const dateValue = new Date(course.startDate);
    if (isNaN(dateValue.getTime())) {
      errors.push({
        field: "startDate",
        message: `Row ${rowDisplay}: Start Date must be a valid date (YYYY-MM-DD)`,
      });
    }
  }

  // Degrees validation (if provided)
  if (course.degrees && course.degrees.length > 0) {
    if (!Array.isArray(course.degrees)) {
      errors.push({
        field: "degrees",
        message: `Row ${rowDisplay}: Degrees must be a comma-separated list of IDs`,
      });
    }
  }

  // Number of Modules validation
  if (course.numberOfModules !== undefined) {
    const modulesValue = parseInt(course.numberOfModules);
    if (isNaN(modulesValue)) {
      errors.push({
        field: "numberOfModules",
        message: `Row ${rowDisplay}: Number of Modules must be a valid number`,
      });
    } else if (modulesValue < 1 || modulesValue > 35) {
      errors.push({
        field: "numberOfModules",
        message: `Row ${rowDisplay}: Number of Modules must be between 1 and 35`,
      });
    }
  }

  // Number of Activities validation
  if (course.numberOfActivities !== undefined) {
    const activitiesValue = parseInt(course.numberOfActivities);
    if (isNaN(activitiesValue)) {
      errors.push({
        field: "numberOfActivities",
        message: `Row ${rowDisplay}: Number of Activities must be a valid number`,
      });
    } else if (activitiesValue < 1 || activitiesValue > 3) {
      errors.push({
        field: "numberOfActivities",
        message: `Row ${rowDisplay}: Number of Activities must be between 1 and 3`,
      });
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Validates an array of course objects
 * @param courses Array of course objects to validate
 * @returns Object containing valid courses and validation errors
 */
export const validateCourses = (
  courses: any[]
): {
  validCourses: any[];
  invalidCourses: any[];
  errors: ValidationError[];
} => {
  const validCourses: any[] = [];
  const invalidCourses: any[] = [];
  const errors: ValidationError[] = [];

  courses.forEach((course, index) => {
    const validationResult = validateCourse(course, index);

    if (validationResult.isValid) {
      validCourses.push(course);
    } else {
      invalidCourses.push({
        ...course,
        errors: validationResult.errors,
      });
      errors.push(...validationResult.errors);
    }
  });

  return {
    validCourses,
    invalidCourses,
    errors,
  };
};
